import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}

const widths = { sm: 'max-w-md', md: 'max-w-lg', lg: 'max-w-2xl', xl: 'max-w-4xl' };

const focusableSelector = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

// Confirmations can be opened from detail dialogs. Only the top-most modal
// should respond to keyboard events in that case.
const openModalStack: symbol[] = [];

export const dialogLabelProps = (title: string, titleId: string): { 'aria-labelledby'?: string; 'aria-label'?: string } =>
  title ? { 'aria-labelledby': titleId } : { 'aria-label': 'Dialog' };

export const containedFocusTarget = (length: number, activeIndex: number, shiftKey: boolean, activeInside = true): number | null => {
  if (length <= 0) return -1;
  if (shiftKey && (!activeInside || activeIndex <= 0)) return length - 1;
  if (!shiftKey && activeIndex === length - 1) return 0;
  return null;
};

export const Modal: React.FC<ModalProps> = ({ open, onClose, title, children, size = 'md' }) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);
  const tokenRef = useRef(Symbol('modal'));
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const reactId = useId();
  const titleId = `intrlock-modal-title-${reactId.replace(/:/g, '')}`;

  useEffect(() => {
    if (!open) return;

    const token = tokenRef.current;
    restoreFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    openModalStack.push(token);

    const dialog = dialogRef.current;
    const initialFocus = dialog?.querySelector<HTMLElement>(focusableSelector) || dialog;
    initialFocus?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (openModalStack[openModalStack.length - 1] !== token) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        onCloseRef.current();
        return;
      }
      if (event.key !== 'Tab' || !dialog) return;

      const focusable = Array.from(dialog.querySelectorAll<HTMLElement>(focusableSelector))
        .filter((node) => !node.hasAttribute('disabled') && node.getAttribute('aria-hidden') !== 'true');
      const activeIndex = focusable.findIndex((node) => node === document.activeElement);
      const targetIndex = containedFocusTarget(focusable.length, activeIndex, event.shiftKey, dialog.contains(document.activeElement));
      if (targetIndex === -1) {
        event.preventDefault();
        dialog.focus();
        return;
      }
      if (targetIndex !== null) {
        event.preventDefault();
        focusable[targetIndex].focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      const index = openModalStack.lastIndexOf(token);
      if (index >= 0) openModalStack.splice(index, 1);
      const previous = restoreFocusRef.current;
      if (previous?.isConnected) previous.focus();
    };
  }, [open]);

  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-50 overflow-y-auto" data-testid="modal-layer">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/50 transition-opacity" aria-hidden="true" onClick={onClose} />
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          {...dialogLabelProps(title, titleId)}
          tabIndex={-1}
          className={`relative bg-white rounded-xl shadow-xl ${widths[size]} w-full p-6 z-10`}
        >
          <div className="flex items-center justify-between mb-4">
            {title && <h2 id={titleId} className="text-lg font-semibold text-gray-900">{title}</h2>}
            <button type="button" onClick={onClose} aria-label={title ? `Close ${title}` : 'Close dialog'} className="ml-auto text-gray-400 hover:text-gray-600 transition-colors">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>
          {children}
        </div>
      </div>
    </div>,
    document.body,
  );
};
